import { useMemo, useState } from "react";
import MediaCard from "@/components/base/MediaCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import Breadcrumb, { type Crumb } from "@/components/base/Breadcrumb";
import type { MediaItem } from "@/types";

interface CategoryBrowserProps {
  items: MediaItem[];
  title: string;
  description: string;
  crumbs: Crumb[];
  initialGenre?: string;
}

type SortKey = "hot" | "year" | "rating";

const PAGE_SIZE = 8;
const ALL = "全部";

export default function CategoryBrowser({
  items,
  title,
  description,
  crumbs,
  initialGenre = ALL,
}: CategoryBrowserProps) {
  const genres = useMemo(
    () => [ALL, ...Array.from(new Set(items.flatMap((i) => i.genres)))],
    [items],
  );
  const regions = useMemo(
    () => [ALL, ...Array.from(new Set(items.map((i) => i.region)))],
    [items],
  );
  const years = useMemo(
    () => [ALL, ...Array.from(new Set(items.map((i) => i.year))).sort((a, b) => b - a)],
    [items],
  );

  const [genre, setGenre] = useState(genres.includes(initialGenre) ? initialGenre : ALL);
  const [region, setRegion] = useState(ALL);
  const [year, setYear] = useState(ALL);
  const [sort, setSort] = useState<SortKey>("hot");
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  const resetFilters = () => {
    setGenre(ALL);
    setRegion(ALL);
    setYear(ALL);
    setSort("hot");
    setPage(1);
  };

  const filtered = useMemo(() => {
    const list = items.filter((item) => {
      if (genre !== ALL && !item.genres.includes(genre)) return false;
      if (region !== ALL && item.region !== region) return false;
      if (year !== ALL && item.year !== Number(year)) return false;
      return true;
    });
    return [...list].sort((a, b) => {
      if (sort === "rating") return b.rating - a.rating;
      if (sort === "year") return b.year - a.year || b.rating - a.rating;
      const hotA = a.rating * 10 + (a.year - 2020);
      const hotB = b.rating * 10 + (b.year - 2020);
      return hotB - hotA;
    });
  }, [items, genre, region, year, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const applyFilter = (fn: () => void) => {
    fn();
    setPage(1);
  };

  const activeFilterCount =
    (genre !== ALL ? 1 : 0) + (region !== ALL ? 1 : 0) + (year !== ALL ? 1 : 0);

  const sortOptions: { key: SortKey; label: string }[] = [
    { key: "hot", label: "按热度" },
    { key: "year", label: "按上映时间" },
    { key: "rating", label: "按评分" },
  ];

  const FilterGroup = ({
    label,
    options,
    value,
    onChange,
  }: {
    label: string;
    options: (string | number)[];
    value: string | number;
    onChange: (v: string) => void;
  }) => (
    <div className="flex flex-col gap-2 border-b border-background-200/70 py-4 last:border-0 sm:flex-row sm:items-start">
      <span className="w-16 shrink-0 pt-1 text-sm font-medium text-foreground-600">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const active = String(value) === String(opt);
          return (
            <button
              key={String(opt)}
              type="button"
              onClick={() => onChange(String(opt))}
              className={`whitespace-nowrap rounded-full px-3 py-1.5 text-sm transition-colors ${
                active
                  ? "bg-primary-600 text-background-50"
                  : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
              }`}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );

  const filterPanel = (
    <div className="rounded-lg border border-background-200/70 bg-background-50 px-4 py-2 md:px-5">
      <FilterGroup
        label="类型"
        options={genres}
        value={genre}
        onChange={(v) => applyFilter(() => setGenre(v))}
      />
      <FilterGroup
        label="地区"
        options={regions}
        value={region}
        onChange={(v) => applyFilter(() => setRegion(v))}
      />
      <FilterGroup
        label="年份"
        options={years}
        value={year}
        onChange={(v) => applyFilter(() => setYear(v))}
      />
    </div>
  );

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb items={crumbs} />

      <div className="mt-4">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">{title}</h1>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">{description}</p>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 rounded-full border border-background-200 bg-background-100/70 p-1">
            {sortOptions.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => applyFilter(() => setSort(opt.key))}
                className={`whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                  sort === opt.key
                    ? "bg-primary-600 text-background-50"
                    : "text-foreground-600 hover:text-foreground-950"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <span className="text-sm text-foreground-500">
            共 <strong className="text-foreground-900">{filtered.length}</strong> 部作品
          </span>
        </div>

        <button
          type="button"
          onClick={() => setFilterOpen(true)}
          className="flex items-center gap-2 rounded-md border border-background-200 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-700 md:hidden"
        >
          <i className="ri-filter-3-line text-base" />
          筛选
          {activeFilterCount > 0 && (
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-accent-500 text-xs text-background-50">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      <div className="mt-4 hidden md:block">{filterPanel}</div>

      <div className="mt-6">
        {paged.length > 0 ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
            {paged.map((item) => (
              <MediaCard key={item.id} item={item} />
            ))}
          </div>
        ) : (
          <EmptyState
            icon="ri-movie-2-line"
            title="未找到符合条件的作品"
            description="当前筛选条件下没有匹配的作品，试试放宽筛选条件，或点击下方按钮重置筛选。"
            actionLabel="重置筛选"
            onAction={resetFilters}
          />
        )}
      </div>

      <Pagination current={currentPage} total={totalPages} onChange={setPage} />

      {filterOpen && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/40"
            onClick={() => setFilterOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute inset-x-0 bottom-0 max-h-[80vh] animate-slide-down overflow-y-auto rounded-t-2xl bg-background-50 p-4">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="font-heading text-lg font-semibold text-foreground-950">筛选条件</h3>
              <button
                type="button"
                aria-label="关闭筛选"
                onClick={() => setFilterOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-700 hover:bg-background-100"
              >
                <i className="ri-close-line text-2xl" />
              </button>
            </div>
            {filterPanel}
            <div className="mt-4 flex gap-3">
              <button
                type="button"
                onClick={resetFilters}
                className="flex-1 rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm font-medium whitespace-nowrap text-foreground-700"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => setFilterOpen(false)}
                className="flex-1 rounded-md bg-primary-600 px-4 py-2.5 text-sm font-medium whitespace-nowrap text-background-50"
              >
                查看结果（{filtered.length}）
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}